import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Tabs,
  Card,
  Row,
  Col,
  Empty,
  Spin,
  Alert,
  Rate,
  Tag,
  Button
} from 'antd';
import {
  ShoppingOutlined,
  CalendarOutlined,
  ShopOutlined,
  HeartOutlined,
  EnvironmentOutlined,
  EuroOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import { getCityByLanguage } from '../../utils/locationFormat';
import './MyFavoritesPage.scss';

const MyFavoritesPage = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { favoriteStore } = useStores();

  useEffect(() => {
    favoriteStore.fetchAllFavorites();
  }, []);

  const renderProductCard = (favorite) => (
    <Col xs={24} sm={12} md={8} lg={6} key={favorite.id}>
      <Card
        hoverable
        className="favorite-card"
        onClick={() => navigate(`/products/${favorite.product_id}`)}
        cover={
          favorite.product_main_image ? (
            <img
              alt={favorite.product_title}
              src={favorite.product_main_image}
              className="card-image"
            />
          ) : (
            <div className="card-image-placeholder">
              <ShoppingOutlined />
            </div>
          )
        }
        actions={[
          <FavoriteButton
            key="favorite"
            type="product"
            itemId={favorite.product_id}
            size="small"
          />
        ]}
      >
        <Card.Meta
          title={favorite.product_title}
          description={
            <div className="card-details">
              {favorite.product_average_rating > 0 && (
                <div className="rating">
                  <Rate disabled allowHalf value={parseFloat(favorite.product_average_rating)} style={{ fontSize: 12 }} />
                </div>
              )}
              {favorite.product_price && (
                <div className="price">
                  <EuroOutlined /> €{parseFloat(favorite.product_price).toFixed(2)}
                </div>
              )}
            </div>
          }
        />
      </Card>
    </Col>
  );

  const renderServiceCard = (favorite) => (
    <Col xs={24} sm={12} md={8} lg={6} key={favorite.id}>
      <Card
        hoverable
        className="favorite-card service-card"
        onClick={() => navigate(`/services/${favorite.service_id}`)}
        actions={[
          <FavoriteButton
            key="favorite"
            type="service"
            itemId={favorite.service_id}
            size="small"
          />
        ]}
      >
        <Tag color="blue" className="category-tag">{favorite.category_name}</Tag>
        <Card.Meta
          title={favorite.service_name}
          description={
            <div className="card-details">
              {favorite.service_average_rating > 0 && (
                <div className="rating">
                  <Rate disabled allowHalf value={parseFloat(favorite.service_average_rating)} style={{ fontSize: 12 }} />
                </div>
              )}
              <div className="info-row">
                <EnvironmentOutlined /> {favorite.partner_name}
              </div>
              <div className="info-row">
                <ClockCircleOutlined /> {favorite.service_duration} min
              </div>
              <div className="price">
                <EuroOutlined /> €{parseFloat(favorite.service_price).toFixed(2)}
              </div>
            </div>
          }
        />
      </Card>
    </Col>
  );

  const renderPartnerCard = (favorite) => (
    <Col xs={24} sm={12} md={8} lg={6} key={favorite.id}>
      <Card
        hoverable
        className="favorite-card partner-card"
        onClick={() => navigate(`/partners/${favorite.partner_id}`)}
        cover={
          favorite.partner_logo ? (
            <img
              alt={favorite.partner_name}
              src={favorite.partner_logo}
              className="card-image"
            />
          ) : (
            <div className="card-image-placeholder">
              <ShopOutlined />
            </div>
          )
        }
        actions={[
          <FavoriteButton
            key="favorite"
            type="partner"
            itemId={favorite.partner_id}
            size="small"
          />
        ]}
      >
        <Card.Meta
          title={favorite.partner_name}
          description={
            <div className="card-details">
              {favorite.is_service_partner && (
                <Tag color="green">{t('favorites.serviceProvider')}</Tag>
              )}
              {favorite.partner_city && (
                <div className="info-row">
                  <EnvironmentOutlined /> {getCityByLanguage(favorite.partner_city, i18n.language)}
                </div>
              )}
            </div>
          }
        />
      </Card>
    </Col>
  );

  if (favoriteStore.loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  const items = [
    {
      key: 'products',
      label: (
        <span>
          <ShoppingOutlined /> {t('favorites.tabs.products', { count: favoriteStore.counts.products })}
        </span>
      ),
      children: favoriteStore.favoriteProducts.length === 0 ? (
        <Empty
          description={t('favorites.noProducts')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => navigate('/products')}>
            {t('favorites.browseProducts')}
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {favoriteStore.favoriteProducts.map(renderProductCard)}
        </Row>
      )
    },
    {
      key: 'services',
      label: (
        <span>
          <CalendarOutlined /> {t('favorites.tabs.services', { count: favoriteStore.counts.services })}
        </span>
      ),
      children: favoriteStore.favoriteServices.length === 0 ? (
        <Empty
          description={t('favorites.noServices')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => navigate('/services')}>
            {t('favorites.browseServices')}
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {favoriteStore.favoriteServices.map(renderServiceCard)}
        </Row>
      )
    },
    {
      key: 'partners',
      label: (
        <span>
          <ShopOutlined /> {t('favorites.tabs.partners', { count: favoriteStore.counts.partners })}
        </span>
      ),
      children: favoriteStore.favoritePartners.length === 0 ? (
        <Empty
          description={t('favorites.noPartners')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => navigate('/products')}>
            {t('favorites.discoverPartners')}
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {favoriteStore.favoritePartners.map(renderPartnerCard)}
        </Row>
      )
    }
  ];

  return (
    <div className="my-favorites-page">
      <div className="page-header">
        <h1><HeartOutlined /> {t('favorites.title')}</h1>
        <p>{t('favorites.description')}</p>
      </div>

      {favoriteStore.error && (
        <Alert
          message={t('common.error')}
          description={favoriteStore.error}
          type="error"
          closable
          onClose={() => favoriteStore.clearError()}
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs
        defaultActiveKey="products"
        items={items}
        size="large"
      />
    </div>
  );
});

export default MyFavoritesPage;
